import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ALL_FIELDS,
  REQUIRED_FIELDS,
  autoDetectMapping,
  type MOZAICField,
} from './pobImport';

const NONE = '__none__';

interface Props {
  filename: string;
  fileColumns: string[];
  initialMap?: Record<string, MOZAICField>;
  onCancel: () => void;
  // mapping returned as { source_col: mozaic_field }
  onConfirm: (params: {
    columnMap: Record<string, MOZAICField>;
    save: boolean;
    name: string;
    makeDefault: boolean;
  }) => void;
}

export default function POBColumnMapper({
  filename,
  fileColumns,
  initialMap,
  onCancel,
  onConfirm,
}: Props) {
  // state keyed by MOZAIC field → source column (or NONE)
  const initialFieldToCol = useMemo(() => {
    const auto = initialMap ?? autoDetectMapping(fileColumns);
    const out: Partial<Record<MOZAICField, string>> = {};
    for (const [col, field] of Object.entries(auto)) {
      out[field as MOZAICField] = col;
    }
    return out;
  }, [fileColumns, initialMap]);

  const [mapping, setMapping] = useState<Partial<Record<MOZAICField, string>>>(
    initialFieldToCol
  );
  const [save, setSave] = useState(false);
  const [name, setName] = useState(`Imported from ${filename}`);
  const [makeDefault, setMakeDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = () => {
    const missing = REQUIRED_FIELDS.filter((f) => !mapping[f]);
    if (missing.length > 0) {
      setError(
        `Please map required fields: ${missing
          .map((f) => ALL_FIELDS.find((a) => a.key === f)!.label)
          .join(', ')}`
      );
      return;
    }
    // invert to { source_col: mozaic_field }
    const columnMap: Record<string, MOZAICField> = {};
    for (const [field, col] of Object.entries(mapping)) {
      if (col && col !== NONE) columnMap[col] = field as MOZAICField;
    }
    onConfirm({ columnMap, save, name: name.trim() || `Imported from ${filename}`, makeDefault });
  };

  return (
    <div className="mt-4 space-y-4">
      <div>
        <h3 className="text-base font-semibold">Map Your Columns</h3>
        <p className="text-sm text-muted-foreground">
          Tell us which column in your file corresponds to each MOZAIC field.
          We'll remember this mapping for next time.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          File: <span className="font-mono">{filename}</span> · {fileColumns.length} columns detected
        </p>
      </div>

      <div className="space-y-2">
        <div className="grid grid-cols-[1fr_1fr] gap-2 border-b pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <div>MOZAIC Field</div>
          <div>Your File Column</div>
        </div>
        {ALL_FIELDS.map((f) => (
          <div key={f.key} className="grid grid-cols-[1fr_1fr] items-center gap-2">
            <Label className="text-sm">
              {f.label}
              {f.required && <span className="ml-1 text-destructive">*</span>}
            </Label>
            <Select
              value={mapping[f.key] ?? NONE}
              onValueChange={(v) =>
                setMapping((prev) => ({
                  ...prev,
                  [f.key]: v === NONE ? undefined : v,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Not in file" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Not in file</SelectItem>
                {fileColumns.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>

      <div className="rounded-md border border-input bg-muted/30 p-3 space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={save}
            onChange={(e) => setSave(e.target.checked)}
          />
          Save this mapping for future imports
        </label>
        {save && (
          <div className="space-y-2 pl-6">
            <div>
              <Label className="text-xs">Mapping name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Acme POB spreadsheet"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={makeDefault}
                onChange={(e) => setMakeDefault(e.target.checked)}
              />
              Use as default mapping for my tenant
            </label>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          className="bg-mozaic-teal hover:bg-mozaic-teal/90"
        >
          Preview Import →
        </Button>
      </div>
    </div>
  );
}
